import { useEffect } from 'react';
import pralineSpinner from './praline-spinner.webp';
import { createPortal } from 'react-dom';

interface SpinnerProps {
  message?: string;
  /** * If true, renders as a full-screen frosted glass overlay
   * that blocks all user interaction.
   */
  global?: boolean;
}

const LoadingSpinner: React.FC<SpinnerProps> = ({
  message = 'Baking Perfection...',
  global = false,
}: SpinnerProps = {}) => {
  // Standard practice: Lock scrolling when a global overlay is active
  useEffect(() => {
    if (global) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [global]);

  // The core spinner UI, extracted so we can render it inline OR in a portal
  const SpinnerContent = (
    <div
      className="flex flex-col items-center justify-center space-y-4"
      role="status"
      aria-live="polite"
    >
      <div className="relative h-32 w-32">
        <img
          src={pralineSpinner}
          alt="Loading delicious treats..."
          className="h-full w-full animate-[spin_3s_linear_infinite] object-contain"
        />
      </div>
      <h4 className="!font-bold !tracking-[0.5px] animate-pulse uppercase">
        {message}
      </h4>
    </div>
  );

  // 1. Render Global Overlay via Portal
  if (global) {
    // bg-white/40 + backdrop-blur-md creates the modern "frosted glass" effect
    const overlay = (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/20 backdrop-blur-md">
        {SpinnerContent}
      </div>
    );

    // Safety check for environments to ensure document exists
    return typeof document !== 'undefined'
      ? createPortal(overlay, document.body)
      : null;
  }

  // 2. Render standard inline spinner
  return SpinnerContent;
};

export default LoadingSpinner;
